import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, Hand, User, Package, FileCheck, Download, AlertCircle, CheckCircle2, Eye, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { getPrendasPendientesEntrega, createEntrega, getHistorialEntregas, buscarEmpleadosPorNombre, getRecolecciones } from '../services/deposito.services';
import { printEntregaReceipt } from '@/utils/thermalPrinter';
import PrintEntregaReceiptButton from './PrintEntregaReceiptButton';
import ModalDetalleRecoleccion from './ModalDetalleRecoleccion';
import { useAuth } from '@/contexts/AuthContext';

const SECTION_ICON_CLASS = 'h-5 w-5 text-primary';

const EntregaPrendas = ({ idPlanta, plantaNombre, onIrAlistamiento }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Búsqueda
  const [modoBusqueda, setModoBusqueda] = useState('cedula'); // 'cedula' | 'nombre'
  const [searchDocument, setSearchDocument] = useState('');
  const [busquedaNombre, setBusquedaNombre] = useState('');
  const [resultadosNombre, setResultadosNombre] = useState([]);
  const [searchingEmpleado, setSearchingEmpleado] = useState(false);
  const [empleadoNoEncontrado, setEmpleadoNoEncontrado] = useState(false);

  const [userData, setUserData] = useState(null);
  const [prendasPendientes, setPrendasPendientes] = useState([]);
  const [prendasSeleccionadas, setPrendasSeleccionadas] = useState([]);
  const [cantidadesEntrega, setCantidadesEntrega] = useState({});
  const signatureRef = useRef(null);
  
  // Estados para pendientes
  const [recoleccionesPendientes, setRecoleccionesPendientes] = useState([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [detalleRecoleccion, setDetalleRecoleccion] = useState(null);
  const [pagePendientes, setPagePendientes] = useState(1);
  const [totalPendientes, setTotalPendientes] = useState(0);
  const PAGE_SIZE_PENDIENTES = 10;

  // Estados para historial
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [paginacion, setPaginacion] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10
  });

  const buscarPorDocumento = async () => {
    if (!searchDocument.trim()) {
      toast({ title: 'Error', description: 'Debe ingresar un número de cédula', variant: 'destructive' });
      return;
    }
    setEmpleadoNoEncontrado(false);
    await cargarPrendasEmpleado(searchDocument.trim(), searchDocument.trim());
  };

  const cargarPrendasEmpleado = async (cedula, nombreEmpleado) => {
    setLoading(true);
    try {
      const response = await getPrendasPendientesEntrega(cedula);
      const prendas = response?.data || [];

      if (prendas.length === 0) {
        setEmpleadoNoEncontrado(true);
        toast({
          title: 'Sin prendas pendientes',
          description: `No se encontraron prendas pendientes de entrega para ${nombreEmpleado}`,
          variant: 'destructive'
        });
        setUserData(null);
        setPrendasPendientes([]);
        setPrendasSeleccionadas([]);
        return;
      }

      setUserData(prendas[0].empleado);
      setPrendasPendientes(prendas);
      setPrendasSeleccionadas([]);
      setCantidadesEntrega({});

      const totalPrendas = prendas.reduce((sum, p) => sum + (p.cantidad || 0), 0);
      toast({
        title: 'Usuario encontrado',
        description: `${prendas.length} tipo(s) de prenda(s) (${totalPrendas} unidades) para entregar`
      });
    } catch (error) {
      console.error('Error buscando prendas:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo buscar la información',
        variant: 'destructive'
      });
      setUserData(null);
      setPrendasPendientes([]);
      setPrendasSeleccionadas([]);
      setCantidadesEntrega({});
    } finally {
      setLoading(false);
    }
  };

  const buscarPorNombre = async () => {
    if (!busquedaNombre.trim() || busquedaNombre.trim().length < 2) {
      toast({ title: 'Error', description: 'Ingrese al menos 2 caracteres para buscar', variant: 'destructive' });
      return;
    }
    setSearchingEmpleado(true);
    setEmpleadoNoEncontrado(false);
    setResultadosNombre([]);
    try {
      const response = await buscarEmpleadosPorNombre(busquedaNombre.trim());
      const lista = response?.data || [];
      if (lista.length === 0) setEmpleadoNoEncontrado(true);
      else setResultadosNombre(lista);
    } catch {
      setEmpleadoNoEncontrado(true);
      toast({ title: 'Error', description: 'No se encontraron empleados', variant: 'destructive' });
    } finally {
      setSearchingEmpleado(false);
    }
  };

  const seleccionarEmpleadoDeLista = async (emp) => {
    setResultadosNombre([]);
    setBusquedaNombre('');
    await cargarPrendasEmpleado(emp.cedula, `${emp.nombre} ${emp.apellido}`);
  };

  const togglePrendaSeleccionada = (prendaId) => {
    setPrendasSeleccionadas(prev => {
      const isCurrentlySelected = prev.includes(prendaId);
      
      if (isCurrentlySelected) {
        return prev.filter(id => id !== prendaId);
      } else {
        // Al seleccionar, inicializar la cantidad con cantidad_pendiente
        const prenda = prendasPendientes.find(p => (p._id || p.id) === prendaId);
        if (prenda) {
          const disponible = prenda.cantidad_pendiente ?? prenda.cantidadPendiente ?? prenda.cantidad ?? 0;
          setCantidadesEntrega(prevCantidades => ({
            ...prevCantidades,
            [prendaId]: disponible
          }));
        }
        return [...prev, prendaId];
      }
    });
  };

  const seleccionarTodas = () => {
    const ids = prendasPendientes.map(p => p._id || p.id);
    setPrendasSeleccionadas(ids);
    // Siempre actualizar con la cantidad pendiente actual
    const nuevasCantidades = {};
    prendasPendientes.forEach((prenda) => {
      const id = prenda._id || prenda.id;
      const disponible = prenda.cantidad_pendiente ?? prenda.cantidadPendiente ?? prenda.cantidad ?? 0;
      nuevasCantidades[id] = disponible;
    });
    setCantidadesEntrega(nuevasCantidades);
  };

  const deseleccionarTodas = () => {
    setPrendasSeleccionadas([]);
  };

  const handleCantidadEntregaChange = (prendaId, disponible, value) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return;

    const ajustado = Math.max(0, Math.min(parsed, disponible));
    setCantidadesEntrega((prev) => ({
      ...prev,
      [prendaId]: ajustado,
    }));
  };

  const getCantidadSeleccionada = (prendaId, fallback = 0) => {
    const value = cantidadesEntrega[prendaId];
    return typeof value === 'number' ? value : fallback;
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const saveSignature = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      return signatureData;
    }
    return null;
  };

  const handlePrintEntrega = () => {
    if (!userData || prendasSeleccionadas.length === 0) {
      toast({
        title: 'Error',
        description: 'No hay prendas seleccionadas para imprimir',
        variant: 'destructive'
      });
      return;
    }

    const prendasAImprimir = prendasPendientes
      .filter(p => prendasSeleccionadas.includes(p._id || p.id))
      .map((prenda) => {
        const prendaId = prenda._id || prenda.id;
        const original = prenda.cantidadOriginal ?? prenda.cantidad ?? 0;
        const disponible = prenda.cantidadPendiente ?? prenda.cantidad ?? original;
        const cantidadEntregar = getCantidadSeleccionada(prendaId, disponible);
        return {
          ...prenda,
          cantidadOriginal: original,
          cantidadEntregar,
          cantidadPendiente: Math.max(disponible - cantidadEntregar, 0),
        };
      });

    const entregaData = {
      empleado: userData,
      prendas: prendasAImprimir,
      planta: plantaNombre,
      entregadoPor: user?.name || 'Sistema'
    };

    printEntregaReceipt(entregaData);
  };

  const procesarEntrega = async () => {
    if (prendasSeleccionadas.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe seleccionar al menos una prenda para entregar',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const signatureData = saveSignature();
      
      // Preparar prendas a entregar
      const prendasAEntregar = prendasPendientes
        .filter(p => prendasSeleccionadas.includes(p._id || p.id))
        .map((prenda) => {
          const prendaId = prenda._id || prenda.id;
          const original = prenda.cantidadOriginal ?? prenda.cantidad ?? 0;
          const disponible = prenda.cantidadPendiente ?? prenda.cantidad ?? original;
          const cantidadSeleccionada = getCantidadSeleccionada(prendaId, disponible);

          if (cantidadSeleccionada <= 0) {
            throw new Error(`La prenda ${prenda.nombre} debe tener una cantidad a entregar mayor a cero.`);
          }

          if (cantidadSeleccionada > disponible) {
            throw new Error(`La cantidad seleccionada para ${prenda.nombre} supera las unidades disponibles (${disponible}).`);
          }

          return {
            ...prenda,
            cantidadOriginal: original,
            cantidadEntregar: cantidadSeleccionada,
            cantidadPendiente: Math.max(disponible - cantidadSeleccionada, 0),
          };
        });

      const totalUnidades = prendasAEntregar.reduce((sum, p) => sum + p.cantidadEntregar, 0);

      // Crear documento de entrega
      const entregaData = {
        empleado: {
          id: userData.id || userData._id,
          nombre: userData.nombre,
          cedula: userData.cedula,
          celular: userData.celular,
          area: userData.area
        },
        prendas: prendasAEntregar.map(p => ({
          prendaRecoleccionId: p._id || p.id,
          recoleccionId: p.recoleccion?._id?.$oid || p.recoleccion?._id || p.recoleccionId,
          recoleccionConsecutivo: p.recoleccion?.consecutivo || p.recoleccionConsecutivo,
          nombre: p.nombre,
          descripcion: p.descripcion,
          cantidad: p.cantidadEntregar,
          cantidadEntregada: p.cantidadEntregar,
          cantidadPendiente: p.cantidadPendiente,
          cantidadOriginal: p.cantidadOriginal ?? p.cantidad ?? p.cantidadEntregar,
          observaciones: p.observaciones,
          peso: p.peso
        })),
        cantidadTotal: totalUnidades,
        fechaEntrega: new Date().toISOString(),
        entregadoPor: {
          id: user.id,
          nombre: user.name
        },
        firmaDigital: signatureData,
        planta: {
          id: idPlanta,
          nombre: plantaNombre
        }
      };

      // Crear entrega en la base de datos
      const response = await createEntrega(entregaData);
      
      toast({
        title: 'Entrega completada',
        description: `${prendasSeleccionadas.length} tipo(s) de prenda(s) (${totalUnidades} unidades) entregadas exitosamente. Consecutivo: ${response.data.consecutivo}`
      });

      // Limpiar formulario
      setSearchDocument('');
      setUserData(null);
      setPrendasPendientes([]);
      setPrendasSeleccionadas([]);
      clearSignature();
      
      // Recargar historial y pendientes
      loadHistorial();
      setPagePendientes(1);
      loadRecoleccionesPendientes(1);

    } catch (error) {
      console.error('Error procesando entrega:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo completar la entrega',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getTotalPrendas = () => {
    return prendasPendientes
      .filter(p => prendasSeleccionadas.includes(p._id || p.id))
      .reduce((total, p) => {
        const prendaId = p._id || p.id;
        const original = p.cantidadOriginal ?? p.cantidad ?? 0;
        const disponible = p.cantidadPendiente ?? p.cantidad ?? original;
        const cantidadSeleccionada = getCantidadSeleccionada(prendaId, disponible);
        return total + cantidadSeleccionada;
      }, 0);
  };

  const loadHistorial = async (page = 1) => {
    setLoadingHistorial(true);
    try {
      const response = await getHistorialEntregas({
        idPlanta,
        limit: paginacion.itemsPerPage,
        offset: page
      });

      setHistorial(response?.data || []);
      setPaginacion({
        currentPage: page,
        totalPages: Math.ceil((response?.totalRecords || 0) / paginacion.itemsPerPage),
        totalRecords: response?.totalRecords || 0,
        itemsPerPage: paginacion.itemsPerPage
      });
    } catch (error) {
      console.error('Error cargando historial:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el historial de entregas',
        variant: 'destructive'
      });
    } finally {
      setLoadingHistorial(false);
    }
  };

  const loadRecoleccionesPendientes = async (page = 1) => {
    if (!idPlanta) return;
    setLoadingPendientes(true);
    try {
      const response = await getRecolecciones({ idPlanta, estado: 'recibido', limit: PAGE_SIZE_PENDIENTES, offset: page });
      setRecoleccionesPendientes(response?.data || []);
      setTotalPendientes(response?.totalRecords ?? 0);
    } catch (error) {
      console.error('Error cargando pendientes:', error);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const handleIniciarEntrega = async (recoleccion) => {
    const cedula = recoleccion.empleado?.cedula;
    const nombre = `${recoleccion.empleado?.nombre || ''} ${recoleccion.empleado?.apellido || ''}`.trim();
    if (!cedula) return;
    setModoBusqueda('cedula');
    setSearchDocument(cedula);
    await cargarPrendasEmpleado(cedula, nombre);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cargar historial al montar
  React.useEffect(() => {
    loadHistorial();
  }, [idPlanta]);

  // Cargar pendientes al montar y al cambiar de página
  React.useEffect(() => {
    loadRecoleccionesPendientes(pagePendientes);
  }, [idPlanta, pagePendientes]);


  return (
    <div className="space-y-2">
      {/* Búsqueda */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Search className={SECTION_ICON_CLASS} />
              Buscar Usuario
            </CardTitle>
            <div className="flex items-center border rounded-lg overflow-hidden text-sm">
              <button
                type="button"
                onClick={() => { setModoBusqueda('cedula'); setEmpleadoNoEncontrado(false); setResultadosNombre([]); }}
                className={`px-3 py-1.5 transition-colors ${modoBusqueda === 'cedula' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                Por cédula
              </button>
              <button
                type="button"
                onClick={() => { setModoBusqueda('nombre'); setEmpleadoNoEncontrado(false); setResultadosNombre([]); }}
                className={`px-3 py-1.5 transition-colors ${modoBusqueda === 'nombre' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-800 text-muted-foreground hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                Por nombre
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {modoBusqueda === 'cedula' && (
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="documento">Número de Documento</Label>
                <Input
                  id="documento"
                  placeholder="Ingrese el número de documento del usuario"
                  value={searchDocument}
                  onChange={(e) => setSearchDocument(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && buscarPorDocumento()}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={buscarPorDocumento} disabled={loading}>
                  <Search className="h-4 w-4 mr-2" />
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
            </div>
          )}

          {modoBusqueda === 'nombre' && (
            <div className="space-y-3">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="busquedaNombre">Nombre o apellido</Label>
                  <Input
                    id="busquedaNombre"
                    placeholder="Ingrese nombre o apellido del usuario"
                    value={busquedaNombre}
                    onChange={(e) => setBusquedaNombre(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && buscarPorNombre()}
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={buscarPorNombre} disabled={searchingEmpleado || loading}>
                    <Search className="h-4 w-4 mr-2" />
                    {searchingEmpleado ? 'Buscando...' : 'Buscar'}
                  </Button>
                </div>
              </div>

              {resultadosNombre.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs font-medium text-muted-foreground border-b">
                    {resultadosNombre.length} resultado(s) — seleccione uno
                  </div>
                  <div className="max-h-48 overflow-y-auto divide-y">
                    {resultadosNombre.map((emp) => (
                      <button
                        key={emp._id || emp.id}
                        type="button"
                        onClick={() => seleccionarEmpleadoDeLista(emp)}
                        disabled={loading}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                      >
                        <div>
                          <p className="font-medium text-sm">{emp.nombre} {emp.apellido}</p>
                          <p className="text-xs text-muted-foreground">Cédula: {emp.cedula}</p>
                        </div>
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                          {emp.area?.nombre || 'Sin área'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {empleadoNoEncontrado && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                ❌ No se encontró ningún empleado con {modoBusqueda === 'cedula' ? `la cédula "${searchDocument}"` : `el nombre "${busquedaNombre}"`}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Información del Usuario */}
      {userData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className={SECTION_ICON_CLASS} />
              Información del Usuario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Nombre</Label>
                <p className="font-medium">{userData.nombre + ' ' + userData.apellido}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Cédula</Label>
                <p className="font-medium">{userData.cedula}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Celular</Label>
                <p className="font-medium">{userData.celular}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Prendas Pendientes */}
      {prendasPendientes.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className={SECTION_ICON_CLASS} />
                  Prendas Pendientes de Entrega
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Seleccione las prendas y cantidades a entregar
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={seleccionarTodas}>
                  Seleccionar Todas
                </Button>
                <Button variant="outline" size="sm" onClick={deseleccionarTodas}>
                  Deseleccionar Todas
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {prendasPendientes.map((prenda) => {
                const prendaId = prenda._id || prenda.id;
                const isSelected = prendasSeleccionadas.includes(prendaId);
                
                return (
                  <motion.div
                    key={prendaId}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => togglePrendaSeleccionada(prendaId)}
                    className={`group relative rounded-md border transition-all cursor-pointer ${
                      isSelected
                        ? 'border-green-500 bg-green-50/50 dark:bg-green-900/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="p-3">
                      <div className="flex items-start gap-2.5">
                        {/* Checkbox */}
                        <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors shrink-0 ${
                          isSelected
                            ? 'border-green-500 bg-green-500'
                            : 'border-gray-300 group-hover:border-gray-400'
                        }`}>
                          {isSelected && (
                            <svg className="w-2.5 h-2.5 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          )}
                        </div>

                        {/* Contenido */}
                        <div className="flex-1 min-w-0">
                          {/* Header de la prenda */}
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-sm truncate">{prenda.nombre}</h4>
                                <Badge variant="outline" className="text-xs font-mono shrink-0">
                                  {prenda.recoleccion?.consecutivo || 'N/A'}
                                </Badge>
                              </div>
                              {prenda.descripcion && (
                                <p className="text-xs text-muted-foreground truncate">{prenda.descripcion}</p>
                              )}
                            </div>
                          </div>

                          {/* Información de cantidades - Inline */}
                          <div className="flex items-center gap-3 text-xs mb-2">
                            <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                              <span className="text-muted-foreground">Total:</span>
                              <span className="font-semibold">{prenda.cantidad}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                              <span className="text-muted-foreground">Entregadas:</span>
                              <span className="font-semibold">{prenda.cantidad_entregada || 0}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                              <span className="text-muted-foreground">Pendientes:</span>
                              <span className="font-semibold text-orange-600">{prenda.cantidadPendiente ?? prenda.cantidad ?? 0}</span>
                            </div>
                          </div>

                          {/* Input de cantidad a entregar */}
                          {isSelected && (
                            <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border text-xs">
                              <Label className="font-medium whitespace-nowrap">A entregar:</Label>
                              <Input
                                type="number"
                                min={0}
                                max={prenda.cantidadPendiente ?? prenda.cantidad ?? 0}
                                value={getCantidadSeleccionada(prendaId, prenda.cantidadPendiente ?? prenda.cantidad ?? 0)}
                                onClick={(event) => event.stopPropagation()}
                                onFocus={(event) => event.stopPropagation()}
                                onChange={(event) => handleCantidadEntregaChange(prendaId, prenda.cantidadPendiente ?? prenda.cantidad ?? 0, event.target.value)}
                                className="w-16 h-7 text-center font-semibold text-xs"
                              />
                              <span className="text-muted-foreground">
                                → Quedarán <span className="font-semibold text-orange-600">{Math.max((prenda.cantidadPendiente ?? prenda.cantidad ?? 0) - getCantidadSeleccionada(prendaId, prenda.cantidadPendiente ?? prenda.cantidad ?? 0), 0)}</span>
                              </span>
                            </div>
                          )}

                          {/* Observaciones */}
                          {prenda.observaciones && (
                            <div className="mt-1.5 flex items-start gap-1.5 text-xs text-muted-foreground">
                              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                              <span className="line-clamp-1">{prenda.observaciones}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {prendasSeleccionadas.length > 0 && (
              <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                    <Package className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-blue-900 dark:text-blue-100">
                      {prendasSeleccionadas.length} tipo(s) seleccionado(s)
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Total: <span className="font-semibold">{getTotalPrendas()}</span> unidades a entregar
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Firma Digital 
      {prendasSeleccionadas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-orange-500" />
              Firma de Recepción
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                El usuario debe firmar como evidencia de que recibió las prendas seleccionadas.
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    width: 500,
                    height: 200,
                    className: 'signature-canvas w-full border rounded'
                  }}
                />
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearSignature}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Limpiar Firma
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
*/}
      {/* Botones de Entrega */}
      {prendasSeleccionadas.length > 0 && (
        <div className="flex justify-end gap-3">
          <Button 
            variant="outline"
            onClick={handlePrintEntrega}
            disabled={loading}
          >
            <Download className="h-4 w-4 mr-2" />
            Imprimir Recibo
          </Button>
          <Button 
            onClick={procesarEntrega} 
            disabled={loading}
            className="min-w-[200px] bg-green-600 hover:bg-green-700"
          >
            <Hand className="h-4 w-4 mr-2" />
            {loading ? 'Procesando...' : 'Completar Entrega'}
          </Button>
        </div>
      )}

      {/* Pendientes de Entrega */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className={SECTION_ICON_CLASS} />
            Pendientes de Entrega
            {totalPendientes > 0 && (
              <Badge variant="secondary">{totalPendientes}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPendientes ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : recoleccionesPendientes.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-sm">Consecutivo</th>
                      <th className="text-left p-3 font-medium text-sm">Empleado</th>
                      <th className="text-left p-3 font-medium text-sm">Cédula</th>
                      <th className="text-left p-3 font-medium text-sm">Área</th>
                      <th className="text-left p-3 font-medium text-sm">Prendas</th>
                      <th className="text-left p-3 font-medium text-sm">Fecha Recepción</th>
                      <th className="text-center p-3 font-medium text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recoleccionesPendientes.map((rec) => {
                      const diasTranscurridos = rec.createdAt
                        ? Math.floor((Date.now() - new Date(rec.createdAt)) / 86400000)
                        : null;
                      return (
                        <tr key={rec._id || rec.id} className="border-b hover:bg-accent/50">
                          <td className="p-3 text-sm">
                            <Badge variant="outline" className="font-mono">{rec.consecutivo || 'N/A'}</Badge>
                          </td>
                          <td className="p-3 text-sm font-medium">
                            {`${rec.empleado?.nombre || ''} ${rec.empleado?.apellido || ''}`.trim() || 'N/A'}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{rec.empleado?.cedula || 'N/A'}</td>
                          <td className="p-3 text-sm text-muted-foreground">{rec.empleado?.area?.nombre || 'N/A'}</td>
                          <td className="p-3 text-sm">
                            <Badge variant="secondary">{rec.cantidad_prendas || 0} uds.</Badge>
                          </td>
                          <td className="p-3 text-sm">
                            <div className="flex flex-col">
                              <span>
                                {rec.createdAt
                                  ? new Date(rec.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                  : 'N/A'}
                              </span>
                              {diasTranscurridos !== null && (
                                <span className={`text-xs font-medium ${diasTranscurridos >= 7 ? 'text-red-600' : diasTranscurridos >= 3 ? 'text-orange-500' : 'text-muted-foreground'}`}>
                                  {diasTranscurridos === 0 ? 'Hoy' : `Hace ${diasTranscurridos} día${diasTranscurridos !== 1 ? 's' : ''}`}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-sm text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => setDetalleRecoleccion(rec)}
                                title="Ver detalle"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                onClick={() => onIrAlistamiento?.(rec.empleado?.cedula)}
                                title="Ir a alistamiento"
                              >
                                <Layers className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleIniciarEntrega(rec)}
                                disabled={loading}
                                title="Iniciar entrega"
                              >
                                <Hand className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Math.ceil(totalPendientes / PAGE_SIZE_PENDIENTES) > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((pagePendientes - 1) * PAGE_SIZE_PENDIENTES) + 1} - {Math.min(pagePendientes * PAGE_SIZE_PENDIENTES, totalPendientes)} de {totalPendientes} pendientes
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagePendientes(p => p - 1)}
                      disabled={pagePendientes === 1 || loadingPendientes}
                    >
                      Anterior
                    </Button>
                    <div className="flex items-center gap-2">
                      {Array.from({ length: Math.min(5, Math.ceil(totalPendientes / PAGE_SIZE_PENDIENTES)) }, (_, i) => {
                        const totalPages = Math.ceil(totalPendientes / PAGE_SIZE_PENDIENTES);
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagePendientes <= 3) {
                          pageNum = i + 1;
                        } else if (pagePendientes >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = pagePendientes - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={pagePendientes === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPagePendientes(pageNum)}
                            disabled={loadingPendientes}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagePendientes(p => p + 1)}
                      disabled={pagePendientes >= Math.ceil(totalPendientes / PAGE_SIZE_PENDIENTES) || loadingPendientes}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay recepciones pendientes de entrega</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historial de Entregas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className={SECTION_ICON_CLASS} />
            Historial de Entregas Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHistorial ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : historial.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 font-medium text-sm">Consecutivo</th>
                      <th className="text-left p-3 font-medium text-sm">Fecha Entrega</th>
                      <th className="text-left p-3 font-medium text-sm">Empleado</th>
                      <th className="text-left p-3 font-medium text-sm">Cédula</th>
                      <th className="text-left p-3 font-medium text-sm">Total Prendas</th>
                      <th className="text-left p-3 font-medium text-sm">Entregado Por</th>
                      <th className="text-center p-3 font-medium text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((entrega, idx) => (
                      <tr key={entrega._id || idx} className="border-b hover:bg-accent/50">
                        <td className="p-3 text-sm">
                          <Badge variant="default" className="font-mono">{entrega.consecutivo}</Badge>
                        </td>
                        <td className="p-3 text-sm">
                          {entrega.fechaEntrega 
                            ? new Date(entrega.fechaEntrega).toLocaleString('es-CO', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'N/A'
                          }
                        </td>
                        <td className="p-3 text-sm">{entrega.empleado?.nombre || 'N/A'}</td>
                        <td className="p-3 text-sm">{entrega.empleado?.cedula || 'N/A'}</td>
                        <td className="p-3 text-sm">
                          <Badge variant="secondary">{entrega.cantidadTotal || 0} unidades</Badge>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{entrega.entregadoPor?.nombre || 'N/A'}</td>
                        <td className="p-3 text-sm">
                          <div className="flex justify-center">
                            <PrintEntregaReceiptButton 
                              entrega={entrega}
                              variant="outline"
                              size="sm"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {paginacion.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {((paginacion.currentPage - 1) * paginacion.itemsPerPage) + 1} - {Math.min(paginacion.currentPage * paginacion.itemsPerPage, paginacion.totalRecords)} de {paginacion.totalRecords} entregas
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadHistorial(paginacion.currentPage - 1)}
                      disabled={paginacion.currentPage === 1 || loadingHistorial}
                    >
                      Anterior
                    </Button>
                    <div className="flex items-center gap-2">
                      {Array.from({ length: Math.min(5, paginacion.totalPages) }, (_, i) => {
                        let pageNum;
                        if (paginacion.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (paginacion.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (paginacion.currentPage >= paginacion.totalPages - 2) {
                          pageNum = paginacion.totalPages - 4 + i;
                        } else {
                          pageNum = paginacion.currentPage - 2 + i;
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={paginacion.currentPage === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => loadHistorial(pageNum)}
                            disabled={loadingHistorial}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadHistorial(paginacion.currentPage + 1)}
                      disabled={paginacion.currentPage === paginacion.totalPages || loadingHistorial}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay entregas registradas</p>
            </div>
          )}
        </CardContent>
      </Card>

      <ModalDetalleRecoleccion
        isOpen={!!detalleRecoleccion}
        onClose={() => setDetalleRecoleccion(null)}
        recoleccion={detalleRecoleccion}
      />
    </div>
  );
};

export default EntregaPrendas;
